<template>
  <app-content>
    <b-container fluid class="alert-warning-report">
      <b-row>
        <b-col>
          <reports-filter
            is-company-field-required
            is-installation-field-required
            is-asset-field-required
            :filter.sync="reportsFilter"
            :filter-objects.sync="reportsFilterObjects"
            @update:filter="onUpdateReportsFilter"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <tag-options-input
            class="mb-3"
            is-required-field
            :label="$t('reports.alarms-and-warnings.filter.module')"
            :tag-label="'name'"
            :track-tag-by="'id'"
            :tags="modules"
            :selected-items.sync="selectedModules"
            :options-custom-label="$t('reports.filters.options.all-modules')"
            :disabled="isModulesDisabled"
            :loading="loadingModules"
            @update:selectedItems="onUpdateModules"
          />
        </b-col>
        <b-col>
          <tag-options-input
            class="mb-4"
            is-required-field
            :label="$t('reports.alarms-and-warnings.filter.alarm-classifications')"
            :tag-label="'name'"
            :track-tag-by="'id'"
            :tags="alarmsClassifications"
            :selected-items.sync="selectedAlarmClassifications"
            :options-custom-label="$t('reports.filters.options.all-classifications')"
            @update:selectedItems="onSelectAlarmClassification"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <tag-options-input
            class="mb-3"
            is-required-field
            :label="$t('reports.alarms-and-warnings.filter.alarm-status')"
            :tag-label="'name'"
            :track-tag-by="'id'"
            :tags="alarmsStatus"
            :selected-items.sync="selectedAlarmStatus"
            :options-custom-label="$t('reports.alarms-and-warnings.filter.all-status')"
            @update:selectedItems="onSelectAlarmStatus"
          />
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col>
          <strong>{{ $t('reports.alarms-and-warnings.filter.comment') }}</strong>
          <b-form-textarea
            class="mt-1"
            v-model="reportsFilter.description"
            maxlength="400"
            :rows="3"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <strong>{{ $t('reports.alarms-and-warnings.filter.alarm') }}</strong>
          <alarms-accordion
            :modulesAlarms="modulesAlarms"
            :loading="loading"
            :selected-alarms.sync="selectedAlarms"
            @update:selected-alarms="onUpdateAlarms"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <period-date-select
            class="alert-warning-report__period-select"
            :initialDate.sync="reportsFilter.initialDate"
            :initialTime.sync="reportsFilter.initialTime"
            :finalDate.sync="reportsFilter.finalDate"
            :finalTime.sync="reportsFilter.finalTime"
            :period.sync="reportsFilter.period"
            @update:initialDate="validatePeriodFields($event, 'initialDate')"
            @update:initialTime="validatePeriodFields($event, 'initialTime')"
            @update:finalDate="validatePeriodFields($event, 'finalDate')"
            @update:finalTime="validatePeriodFields($event, 'finalTime')"
            @update:initialDateTime="onUpdateInitialDateTime($event)"
            @update:finalDateTime="onUpdateFinalDateTime($event)"
          />
        </b-col>
      </b-row>
      <b-row class="mt-5">
        <b-col>
          <report-footer-buttons
            :saveTemplateDisabled="saveTemplateLoading
              || !isPeriodValid
              || !isPeriodRangeValid
              || !isAlarmsLimitValid"
            :viewReportDisabled="!isPeriodValid || !isPeriodRangeValid || !isAlarmsLimitValid"
            :exportXlsxDisabled="disableExport || !isPeriodRangeValid || !isAlarmsLimitValid"
            :saveTemplateLoading="saveTemplateLoading"
            :exportXlsxLoading="exportXlsxLoading"
            :showAdditionalFields="true"
            :sendAssetTypesByFilter="assetTypesByFilter"
            @cancel="cancel"
            @save-template="openModal"
            @view-report="generateReport(openReport)"
            @export-xlsx="generateReport(exportXlsx)"
            @set-additional-fields="setAdditionalFields"
          />
        </b-col>
      </b-row>
    </b-container>
    <save-template-modal
      v-model="showModal"
      :name.sync="reportsFilter.name"
      :is-loading="saveTemplateLoading"
      @input="onTemplateModalInput"
      @save="saveHandler"
    />
  </app-content>
</template>
<script>
import uniq from 'lodash/uniq';
import PeriodDateSelect from '@/components/PeriodDateSelect';
import ReportsFilter from '@/components/ReportsFilter';
import TagOptionsInput from '@/components/inputs/TagOptionsInput';
import AlarmsAndWarningsService from '@/domain/services/generalPanel/reports/alarmsAndWarnings';
import periods from '@/domain/enums/periods';
import { grammaticalJoin } from '@/services/arrayUtils';
import LoadingMixin from '@/services/loadingMixin';
import GenerateReportMixin from '@/domain/mixins/generalPanel/reports/generateReportMixin';
import alarmsStatusLookup from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsStatusLookup';
import alarmsClassificationsLookup from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsClassificationsLookup';
import AlarmsReportMixin from '@/domain/mixins/generalPanel/reports/alarmsReportMixin';
import ModulesService from '@/domain/services/module';
import AlarmsAccordion from '@/domain/components/generalPanel/reports/alarmsAndWarnings/AlarmsAccordion';
import AlarmService from '@/domain/services/generalPanel/mySigma/alarm';
import ReportFooterButtons from '@/app/generalPanel/reports/ReportFooterButtons';
import SaveTemplateModal from '@/app/generalPanel/reports/SaveTemplateModal';
import {
  formatter as dateTimeFormatter,
  joinISODateAndTimeString,
  setToStartOfDay,
  showDiff,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import { mapGetters } from 'vuex';

export default {
  name: 'NewReport',
  components: {
    TagOptionsInput,
    PeriodDateSelect,
    ReportsFilter,
    AlarmsAccordion,
    ReportFooterButtons,
    SaveTemplateModal,
  },
  mixins: [
    LoadingMixin,
    GenerateReportMixin,
    AlarmsReportMixin,
  ],
  data: () => ({
    reportsFilter: {
      name: '',
      period: periods.SEVEN,
      pageSize: 15,
      page: 1,
    },
    reportsFilterObjects: {},
    requiredFields: [
      'companiesIds',
      'installationsIds',
      'assetsIds',
      'modulesIds',
      'status',
      'alarmsIds',
      'classifications',
    ],
    emptyFields: [],
    alarmsStatus: [],
    selectedAlarmStatus: [],
    alarmsClassifications: [],
    selectedAlarmClassifications: [],
    modules: [],
    selectedModules: [],
    loadingModules: false,
    modulesAlarms: [],
    selectedAlarms: [],
    showModal: false,
    exportXlsxLoading: false,
    saveTemplateLoading: false,
    templateIdResponse: '',
    emptyFieldsArray: [],
    isLoadingTemplate: false,
    disableVariableLimit: true,
    originalTemplateName: null,
  }),
  computed: {
    ...mapGetters({
      limitHistoricalPeriodDataInDays: 'limitHistoricalPeriodDataInDays',
      limitVariablesHistoryData: 'limitVariablesHistoryData',
    }),
    assetTypesByFilter() {
      const assets = this.reportsFilterObjects.assets || [];
      return assets.map(asset => (asset.assetType || {}).name);
    },
    disableExport() {
      return !this.isPeriodValid || this.loading;
    },
    isPeriodsUndefined() {
      return (this.reportsFilter.period === periods.PERSONALIZED
        || this.reportsFilter.period === undefined);
    },
    isDateFilled() {
      const {
        initialDate,
        finalDate,
        initialTime,
        finalTime,
      } = this.reportsFilter;
      return initialDate && finalDate && initialTime && finalTime;
    },
    isPeriodValid() {
      return !(this.reportsFilter.period || {}).invalid
        && (this.isDateFilled || this.reportsFilter.period !== periods.PERSONALIZED);
    },
    isModulesDisabled() {
      const { assetsIds } = this.reportsFilter;
      return !(assetsIds || []).length;
    },
    templateId() {
      return this.$route?.params?.relatorioId || this.templateIdResponse;
    },
    isPeriodRangeValid() {
      if (!this.isDateFilled) return true;

      const initialDate = joinISODateAndTimeString(
        this.reportsFilter.initialDate,
        this.reportsFilter.initialTime,
        { timeZone: timezones.UTC },
      );
      const finalDate = joinISODateAndTimeString(
        this.reportsFilter.finalDate,
        this.reportsFilter.finalTime,
        { timeZone: timezones.UTC },
      );
      const diffDate = showDiff(finalDate, initialDate, 'days');
      return diffDate.days <= this.limitHistoricalPeriodDataInDays;
    },
    isAlarmsLimitValid() {
      if (!this.disableVariableLimit) {
        return (this.selectedAlarms || []).length <= this.limitVariablesHistoryData;
      }
      return true;
    },
  },
  created() {
    this.defineAlarmsStatus();
    this.defineAlarmsClassifications();
    this.getTemplateIfEdit();
  },
  methods: {
    getTemplateIfEdit() {
      if (!this.templateId) return;
      this.startLoading();
      this.isLoadingTemplate = true;
      AlarmsAndWarningsService.getTemplateById(this.templateId)
        .then((response) => {
          if (response.partial) {
            this.$pushAlert({ code: 'MI106' });
          }
          this.setFilterByTemplate(response);
        })
        .finally(() => {
          this.endLoading();
          this.$nextTick(() => {
            this.isLoadingTemplate = false;
          });
        });
    },
    setFilterByTemplate(response) {
      this.originalTemplateName = response.name;
      this.reportsFilter.name = response.name;
      this.reportsFilter.description = response.template.description;
      this.reportsFilter.period = response.template.period;
      this.reportsFilter.status = response.template.status;
      this.reportsFilter.classifications = response.template.classifications;

      if (response.template.period === periods.PERSONALIZED) {
        this.onUpdateInitialDateTime(response.template.initialDateTime);
        this.onUpdateFinalDateTime(response.template.finalDateTime);
      }

      this.selectedAlarmStatus = this.alarmsStatus.filter(
        as => response.template.status.includes(as.id),
      );
      this.selectedAlarmClassifications = this.alarmsClassifications.filter(
        ac => response.template.classifications.includes(ac.id),
      );

      this.reportsFilterObjects.companies = response.template.companies;
      this.reportsFilterObjects.regional = response.template.regionals;
      this.reportsFilterObjects.installations = response.template.installations;
      this.reportsFilterObjects.assets = response.template.assets;

      this.reportsFilter.companiesIds = (response.template.companies)
        .map(c => c.id?.toUpperCase());
      this.reportsFilter.regionalIds = (response.template.regionals)
        .map(r => r.id?.toUpperCase());
      this.reportsFilter.installationsIds = (response.template.installations)
        .map(i => i.id?.toUpperCase());
      this.reportsFilter.assetsIds = (response.template.assets)
        .map(a => a.id?.toUpperCase());

      this.getModules();
      this.reportsFilter.modulesIds = (response.template.modulos).map(m => m.id?.toUpperCase());
      this.selectedModules = response.template.modulos;

      this.getAlarmsLookup();
      this.reportsFilter.alarmsIds = response.template.alarmsIds;
      this.selectedAlarms = this.reportsFilter.alarmsIds;

      this.reportsFilter.additionalFields = response.template.additionalFields;
      this.reportsFilterObjects.additionalFields = this.reportsFilter.additionalFields;

      this.reportsFilter = {
        ...this.reportsFilter,
        ...this.splitDateTimes(response.template),
      };
    },
    setAdditionalFields(additionalFields) {
      this.reportsFilter.additionalFields = additionalFields;
    },
    showPeriodValidateMessage() {
      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: grammaticalJoin(this.emptyFieldsArray.map(
            field => this.getFieldLabel(field),
          )),
        },
      });
    },
    validateRequiredFields() {
      const emptyFields = [];

      this.requiredFields.forEach((field) => {
        const value = this.reportsFilter[field];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          emptyFields.push(field);
        }
      });

      if (this.reportsFilter.period === periods.PERSONALIZED) {
        const periodFields = ['initialDate', 'initialTime', 'finalDate', 'finalTime'];
        periodFields.forEach((field) => {
          if (!this.reportsFilter[field]) {
            emptyFields.push(field);
          }
        });
      }

      if (emptyFields.length > 0) {
        this.$pushAlert({
          code: 'MA001',
          messageParams: {
            NomeDoCampo: grammaticalJoin(emptyFields.map(
              field => this.getFieldLabel(field),
            )),
          },
        });
        return false;
      }

      return true;
    },
    onUpdateInitialDateTime(value) {
      this.reportsFilter.initialDateTime = value;
    },
    onUpdateFinalDateTime(value) {
      this.reportsFilter.finalDateTime = value;
    },
    validateDateRange() {
      if (!this.isDateFilled || this.isLoadingTemplate) return;

      const initialDate = joinISODateAndTimeString(
        this.reportsFilter.initialDate,
        this.reportsFilter.initialTime,
        { timeZone: timezones.UTC },
      );
      const finalDate = joinISODateAndTimeString(
        this.reportsFilter.finalDate,
        this.reportsFilter.finalTime,
        { timeZone: timezones.UTC },
      );
      const diffDate = showDiff(finalDate, initialDate, 'days');
      if (Math.ceil(Math.abs(diffDate.days)) > this.limitHistoricalPeriodDataInDays) {
        this.$pushAlert({
          code: 'ME091',
          messageParams: [this.limitHistoricalPeriodDataInDays],
        });
      }
    },
    validatePeriodFields(value, field) {
      if (value) {
        this.emptyFieldsArray = this.emptyFieldsArray.filter(item => item !== field);
      }
      if ((this.reportsFilter.period !== undefined && this.isPeriodsUndefined)
        && !value && !this.emptyFieldsArray.includes(field)) {
        this.emptyFieldsArray.push(field);
        this.emptyFieldsArray = uniq(this.emptyFieldsArray);
        this.showPeriodValidateMessage();
        return;
      }

      if (this.isDateFilled) {
        this.validateDateRange();
      }
    },
    getFieldLabel(field) {
      const fields = {
        companiesIds: this.$t('entities.company'),
        installationsIds: this.$t('entities.installation'),
        assetsIds: this.$t('entities.asset'),
        modulesIds: this.$t('entities.module'),
        alarmsIds: this.$t('reports.alarms-and-warnings.filter.alarm'),
        status: this.$t('reports.alarms-and-warnings.filter.alarm-status'),
        finalDate: this.$t('fields.general.final-date'),
        finalTime: this.$t('fields.general.final-time'),
        initialTime: this.$t('fields.general.initial-time'),
        initialDate: this.$t('fields.general.initial-date'),
        classifications: this.$t('reports.alarms-and-warnings.filter.alarm-classifications'),
      };
      return fields[field];
    },
    openReport() {
      this.openAlarmReport(this.reportsFilter);
    },
    defineAlarmsStatus() {
      this.alarmsStatus = alarmsStatusLookup(this);
      this.selectedAlarmStatus = this.alarmsStatus;
      this.onSelectAlarmStatus();
    },
    defineAlarmsClassifications() {
      this.alarmsClassifications = alarmsClassificationsLookup(this);
      this.selectedAlarmClassifications = this.alarmsClassifications;
      this.onSelectAlarmClassification();
    },
    onSelectAlarmStatus() {
      this.reportsFilter.status = this.selectedAlarmStatus.map(status => status.id);
    },
    onSelectAlarmClassification() {
      this.reportsFilter.classifications = this.selectedAlarmClassifications
        .map(status => status.id);
    },
    filterModules() {
      this.selectedModules = this.selectedModules.filter(
        sm => this.modules.some(
          m => m.id === sm.id,
        ),
      );
    },
    onUpdateModules() {
      this.reportsFilter.modulesIds = (this.selectedModules || [])
        .map(item => item.id);

      this.getAlarmsLookup();
    },
    getModules() {
      this.loadingModules = true;

      const { assetsIds } = this.reportsFilter;

      if (!(assetsIds || []).length) {
        return;
      }

      ModulesService.getModulesLinkedToAlarms(assetsIds)
        .then((response) => {
          this.modules = response;
        })
        .finally(() => {
          this.loadingModules = false;
          this.filterModules();
          this.onUpdateModules();
        });
    },
    onUpdateReportsFilter() {
      const { assetsIds } = this.reportsFilter;

      if ((assetsIds || []).length) {
        this.getModules();

        return;
      }

      this.modules = [];
      this.selectedModules = [];
      this.modulesAlarms = [];
      this.selectedAlarms = [];
    },
    getAlarmsLookup() {
      const { assetsIds, modulesIds } = this.reportsFilter;
      const params = {
        assetsIds,
        modulesIds,
      };

      if (!(assetsIds || []).length
         || !(modulesIds || []).length) {
        this.modulesAlarms = [];
        return;
      }

      AlarmService.lookup(params)
        .then((response) => {
          this.modulesAlarms = response;

          this.filterAlarms();
          this.onUpdateAlarms();
      });
    },
    onUpdateAlarms() {
      this.reportsFilter.alarmsIds = this.selectedAlarms;
    },
    filterAlarms() {
      this.selectedAlarms = this.selectedAlarms.filter(
        sa => this.modulesAlarms.some(
          ma => ma.alarms.some(alarm => alarm.id === sa),
        ),
      );
    },
    cancel() {
      this.$confirmMessage({ code: 'MC006' }).accepted(() => {
        this.$router.push({ name: 'reports.alarms-and-warnings' });
      });
    },
    exportXlsx() {
      this.startLoading();
      this.exportXlsxLoading = true;
      AlarmsAndWarningsService.exportHistoryExcel({ filter: this.reportsFilter })
        .then(() => this.$pushAlert({ code: 'MS011' }))
        .finally(() => {
          this.exportXlsxLoading = false;
          this.endLoading();
        });
    },

    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.onTemplateModalInput(false);
    },
    onTemplateModalInput(value) {
      this.showModal = value;

      if (!value && !this.saveTemplateLoading) {
        this.reportsFilter.name = this.templateId ? this.originalTemplateName : '';
      }
    },
    mountTemplateParams(transient, name) {
      this.reportsFilter.transient = transient;

      this.reportsFilter.initialDateTime = joinISODateAndTimeString(
        this.reportsFilter.initialDate,
        this.reportsFilter.initialTime,
      );
      this.reportsFilter.finalDateTime = joinISODateAndTimeString(
        this.reportsFilter.finalDate,
        this.reportsFilter.finalTime,
      );
      return {
        name,
        template: {
          ...this.reportsFilter,
        },
      };
    },
    insertTemplate(transient = true, templateName = '') {
      const params = this.mountTemplateParams(transient, templateName);
      return AlarmsAndWarningsService.insertTemplate(params);
    },
    editTemplate(templateId, templateName) {
      const params = this.mountTemplateParams(undefined, templateName);
      return AlarmsAndWarningsService.updateTemplate(templateId, params);
    },
    saveTemplate() {
      return this.insertTemplate(false, this.reportsFilter.name);
    },
    saveEditedTemplate() {
      return this.editTemplate(this.templateId, this.reportsFilter.name);
    },
    saveHandler() {
      if (!this.validateRequiredFields()) {
        return;
      }

      if (!this.isPeriodRangeValid) {
        this.$pushAlert({
          code: 'ME091',
          messageParams: [this.limitHistoricalPeriodDataInDays],
        });
        return;
      }

      if (!this.isAlarmsLimitValid) {
        this.$pushAlert({
          code: 'ME090',
          messageParams: [this.limitVariablesHistoryData],
        });
        return;
      }

      const callFunction = this.templateId
        ? this.saveEditedTemplate
        : this.saveTemplate;

      this.saveTemplateLoading = true;

      callFunction()
        .then(() => {
          this.$pushAlert({ code: 'MS006' });
          this.closeModal();
          this.backToList();
        })
        .finally(() => {
          this.saveTemplateLoading = false;
        });
    },
    backToList() {
      return this.$router.push({
        name: 'reports',
        params: {
          activeTabName: 'alarms-and-warnings',
        },
      });
    },
    splitDateTimes(template) {
      return {
        initialDate: setToStartOfDay(template.initialDateTime),
        initialTime: dateTimeFormatter(
          template.initialDateTime,
          formats.ISO_TIME,
        ),
        finalDate: setToStartOfDay(template.finalDateTime),
        finalTime: dateTimeFormatter(template.finalDateTime, formats.ISO_TIME),
      };
    },
  },
  watch: {
    selectedAlarms(newValue) {
      if (this.isLoadingTemplate || this.disableVariableLimit) return;
      if ((newValue || []).length > this.limitVariablesHistoryData) {
        this.$pushAlert({
          code: 'ME090',
          messageParams: [this.limitVariablesHistoryData],
        });
      }
    },
  },
};
</script>
<style lang="sass" scoped>
  .alert-warning-report
    &__period-select
      margin-top: 15px
    &__buttons
      margin-top: 30px
      display: flex
      justify-content: space-between
    &__xlsx-loading
      display: inline-flex
</style>
